/**
 * Mock drill-down "API" (F4.1) — no backend endpoint exists yet.
 *
 * Returns the exact `ChartDrillDownResponse` the backend will, so the canvas
 * swaps this for a real fetch without changing its callers. The live endpoint
 * re-queries the source at the finer grain; this mock synthesises finer rows
 * from the chart's own inline rows instead, so every mock chart can drill
 * without a per-chart fixture.
 */
import type { VisualizationSpec } from "vega-embed";
import type {
  ChartDrillDownRequest,
  ChartDrillDownResponse,
  ChartDrillHierarchy,
  ChartDrillLevel,
  CrossFilterValue,
} from "./crossFilters.types";
import { chartDimensionField, nextDrillLevel } from "./crossFilterMappers";

/** Hierarchies attached to charts until the story API returns `drillHierarchies`. */
export const MOCK_CHART_DRILL_HIERARCHIES: ChartDrillHierarchy[] = [
  {
    id: "geography",
    label: "Geography",
    levels: [
      { field: "region", label: "Region", type: "nominal" },
      { field: "state", label: "State", type: "nominal" },
      { field: "city", label: "City", type: "nominal" },
    ],
  },
  {
    id: "date",
    label: "Date",
    levels: [
      { field: "month", label: "Month", type: "ordinal" },
      { field: "day", label: "Day", type: "ordinal" },
    ],
  },
];

/** Child members per level, keyed by the parent member. */
const MOCK_CHILD_MEMBERS: Record<string, Record<string, string[]>> = {
  state: {
    // Canvas mock (storyCanvasApi.mock) regions.
    Northeast: ["New York", "Massachusetts", "Pennsylvania", "New Jersey"],
    Southeast: ["Florida", "Georgia", "North Carolina", "Virginia"],
    Midwest: ["Illinois", "Ohio", "Michigan", "Indiana"],
    North: ["Illinois", "Michigan", "Minnesota", "Wisconsin"],
    South: ["Texas", "Florida", "Georgia", "Tennessee"],
    East: ["New York", "New Jersey", "Pennsylvania", "Massachusetts"],
    West: ["California", "Washington", "Oregon", "Nevada"],
  },
  city: {
    California: ["Los Angeles", "San Francisco", "San Diego"],
    Texas: ["Houston", "Dallas", "Austin"],
    "New York": ["New York City", "Buffalo", "Rochester"],
  },
};

/** Screenshot days (Sep 02 … Sep 29) reused for every month. */
const MOCK_DAYS_OF_MONTH = ["02", "04", "10", "15", "26", "29"];

/**
 * Deterministic per-child multipliers. They keep each child near its
 * parent's scale, so both sums and percentages still read plausibly.
 */
const MOCK_CHILD_FACTORS = [0.62, 0.78, 0.9, 1.04, 1.12, 1.2];

/** Members one level below `parentValue` at `field`, e.g. West → four states. */
export const mockChildMembers = (field: string, parentValue: CrossFilterValue): string[] => {
  const parent = String(parentValue);
  const fixture = MOCK_CHILD_MEMBERS[field]?.[parent];
  if (fixture) return fixture;
  if (field === "day" && /^\d{4}-\d{2}$/.test(parent)) {
    return MOCK_DAYS_OF_MONTH.map((day) => `${parent}-${day}`);
  }
  // Month-name members — the canvas mock's "Jan" and the Storybook mock's
  // "Sep 24" — read like the screenshot's "Sep 02" axis.
  const monthName = field === "day" ? /^([A-Z][a-z]{2})(?: \d{2})?$/.exec(parent)?.[1] : undefined;
  if (monthName) {
    return MOCK_DAYS_OF_MONTH.map((day) => `${monthName} ${day}`);
  }
  return [1, 2, 3, 4].map((index) => `${parent} ${index}`);
};

type Row = Record<string, unknown>;
type SpecNode = Record<string, unknown>;

const scaleNumbers = (row: Row, factor: number): Row =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [
      key,
      typeof value === "number" ? Math.round(value * factor * 100) / 100 : value,
    ]),
  );

/** Keeps rows at `scope.value`, then splits each into the next level's members. */
const drillRows = (
  rows: Row[],
  scope: ChartDrillDownRequest["scopes"][number],
  next: ChartDrillLevel,
): Row[] =>
  rows
    .filter((row) => String(row[scope.field]) === String(scope.value))
    .flatMap((row) =>
      mockChildMembers(next.field, scope.value).map((member, index) => ({
        ...scaleNumbers(row, MOCK_CHILD_FACTORS[index % MOCK_CHILD_FACTORS.length]),
        [next.field]: member,
      })),
    );

/** Points every channel on `fromField` at `level`, dropping the old channel's timeUnit. */
const retargetEncoding = (encoding: unknown, fromField: string, level: ChartDrillLevel) => {
  if (!encoding || typeof encoding !== "object") return encoding;
  return Object.fromEntries(
    Object.entries(encoding as SpecNode).map(([channel, definition]) => {
      const channelDef = definition as SpecNode | undefined;
      if (!channelDef || channelDef.field !== fromField) return [channel, definition];
      const { timeUnit: _timeUnit, ...rest } = channelDef;
      return [
        channel,
        { ...rest, field: level.field, title: level.label, type: level.type ?? rest.type },
      ];
    }),
  );
};

/** Applies the drill to one spec node and every nested layer, never mutating the source. */
const drillSpecNode = (
  node: SpecNode,
  scopes: ChartDrillDownRequest["scopes"],
  rootField: string,
  finalLevel: ChartDrillLevel,
  hierarchies: ChartDrillHierarchy[],
): SpecNode => {
  const data = node.data as { values?: unknown } | undefined;
  const drilled: SpecNode = { ...node };
  if (data && Array.isArray(data.values)) {
    let rows = data.values as Row[];
    for (const scope of scopes) {
      const next = nextDrillLevel(hierarchies, scope.field);
      if (next) rows = drillRows(rows, scope, next);
    }
    drilled.data = { ...data, values: rows };
  }
  if (node.encoding) drilled.encoding = retargetEncoding(node.encoding, rootField, finalLevel);
  if (Array.isArray(node.layer)) {
    drilled.layer = node.layer.map((layer) =>
      drillSpecNode(layer as SpecNode, scopes, rootField, finalLevel, hierarchies),
    );
  }
  return drilled;
};

/**
 * Mock of the drill-down endpoint. Always drills from the chart's ORIGINAL
 * spec, so stepping back out is just a shorter `scopes` list. A scope at a
 * hierarchy leaf is skipped. With no usable scope, the source spec is
 * returned unchanged.
 */
export const mockChartDrillDown = (
  request: ChartDrillDownRequest,
  sourceSpec: VisualizationSpec,
  hierarchies: ChartDrillHierarchy[] = MOCK_CHART_DRILL_HIERARCHIES,
): ChartDrillDownResponse => {
  const levels = request.scopes
    .map((scope) => nextDrillLevel(hierarchies, scope.field))
    .filter((level): level is ChartDrillLevel => level !== null);
  const rootField = request.scopes[0]?.field;
  const finalLevel = levels[levels.length - 1];

  if (!rootField || !finalLevel) {
    return {
      chartId: request.chartId,
      field: rootField ?? "",
      nextField: nextDrillLevel(hierarchies, rootField)?.field ?? null,
      spec: sourceSpec,
    };
  }

  return {
    chartId: request.chartId,
    field: finalLevel.field,
    nextField: nextDrillLevel(hierarchies, finalLevel.field)?.field ?? null,
    spec: drillSpecNode(
      sourceSpec as SpecNode,
      request.scopes,
      rootField,
      finalLevel,
      hierarchies,
    ) as VisualizationSpec,
  };
};
